import {
  AnyAction,
  configureStore,
  Middleware,
  Reducer,
  StoreEnhancer,
} from '@reduxjs/toolkit'
import querySerializer from 'query-string'
import {
  connectRoutes,
  LocationState,
  Options,
  RoutesMap,
} from 'redux-first-router'
import restoreScroll from 'redux-first-router-restore-scroll'
import reduxThunk from 'redux-thunk'

import {
  clearNavigationStateAction,
  hideBlockNavigationModalAction,
  showBlockNavigationModalAction,
} from '~shared-components/redux/actions/navigation.actions'
import {
  createSyncRoutesMiddleware,
  extendLocationReducer,
} from '~shared-components/redux/reducers/location.reducer'
import { NavigationBlockType } from '~shared-components/redux/reducers/navigation.reducers'
import { SharedState } from '~shared-components/redux/sharedState'

import {
  heapIdentifyUserMiddleware,
  persistCookieMiddleware,
  triggerClientThunkMiddleware,
} from './middleware'
import * as reducers from './reducers'

export interface State extends SharedState {
  title: string
}

export interface HotProps {
  init: () => void
  path: string
}

export interface HotNodeModule extends NodeModule {
  hot?: {
    accept: (path: string, cb: Function) => void
  }
}

export function moduleHotAccept(mod: HotNodeModule, params: HotProps) {
  if (mod && mod.hot) {
    mod.hot.accept(params.path, params.init)
  }
}

export default (
  preloadedState: Partial<State> | undefined,
  opts: Options,
  routeMap: RoutesMap,
) => {
  const connectRouteOptions: Options = {
    ...opts,
    displayConfirmLeave: (
      navType: NavigationBlockType | string,
      callback: (isAllowed: boolean) => void,
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const { dispatch } = store

      const canLeave = (isAllowedToLeave: boolean) => {
        // Clear modal and navigation state to prevent modal from popping up again
        if (isAllowedToLeave) dispatch(clearNavigationStateAction())

        dispatch(hideBlockNavigationModalAction())
        return callback(isAllowedToLeave)
      }

      dispatch(
        showBlockNavigationModalAction({
          canLeave,
          navType: navType as NavigationBlockType,
        }),
      )
    },
    querySerializer,
    restoreScroll: restoreScroll(),
  }
  const { enhancer, middleware, reducer, thunk } = connectRoutes(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    routeMap as any,
    connectRouteOptions,
  )

  // SerializableCheck options are stop internal SerializableStateInvariantMiddleware
  // from complaining not serializable values in the actions and state!

  //  Disabling the check for this the known non-serializable actions and states:
  // - redux-first-router which uses thunks in the state and this is is no-no
  //   https://stackoverflow.com/questions/59773345/react-toolkit-and-redux-first-router
  // - Toaster actions use ReactNode in the message which contains Symbols
  // - in Cart we are using Dinero for prices
  // - in Cart products may contain circular references to itself
  const store = configureStore<State, AnyAction, Middleware<State>[]>({
    devTools: process.env.NODE_ENV === 'development',
    enhancers: defaultEnhancers => [
      enhancer as StoreEnhancer,
      ...defaultEnhancers,
    ],
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck:
          process.env.NODE_ENV === 'development'
            ? {
                ignoredActionPaths: [
                  // router
                  'meta.location.prev',
                  'meta.location.current',
                  'meta.query',
                  'location.routesMap',
                  // toaster
                  'message.$$typeof',
                  // cart
                  'payload.product.data.priceTier.price',
                  'payload.carts',
                  'payload.product.price',
                  'payload.totalValue',
                  'payload.currentCart',
                  //cart modals
                  'payload.product.data.salesGroupProducts',
                  'payload.product.data.customerContextProducts',
                ],
                ignoredActions: [
                  'toaster/addToastAction',
                  'toaster/removeToastAction',
                  'SYNC_ROUTE_MAP_UPDATE',
                ],
                ignoredPaths: [
                  'cart.carts',
                  'location.routesMap',
                  'location.query',
                  'location.prev.query',
                  'message.$$typeof',
                  'cartModal.product.salesGroupProducts',
                  'cartModal.product.customerContextProducts',
                  'cartModal.product.priceTier.price',
                ],
              }
            : false,
      }).prepend(
        middleware,
        createSyncRoutesMiddleware(routeMap),
        heapIdentifyUserMiddleware,
        persistCookieMiddleware,
        triggerClientThunkMiddleware,
        reduxThunk,
      ),
    preloadedState,
    reducer: {
      ...reducers,
      location: extendLocationReducer(reducer) as Reducer<LocationState>,
    },
  })

  // this piece of code enables hot reload of state with redux
  if (process.env.NODE_ENV === 'development') {
    const hotParams = {
      init: () => 0,
      path: './reducers/index',
    }
    moduleHotAccept(module, hotParams)
  }

  return { store, thunk }
}
